import {
  DEFAULT_ADMIN_CONFIGURATIONS,
  MAX_RELIABILITY_COUNTDOWN_SECONDS,
  MAX_RELIABILITY_ERROR_PERCENTAGE,
  MAX_RELIABILITY_PRESENTATIONS,
  MIN_RELIABILITY_COUNTDOWN_SECONDS,
  MIN_RELIABILITY_ERROR_PERCENTAGE,
  MIN_RELIABILITY_PRESENTATIONS,
  OCULAR_MOTILITY_DECIMAL_PLACES,
  OCULAR_MOTILITY_FIELD_BOUNDS,
  SMOOTH_PURSUIT_FIELD_BOUNDS,
  RELIABILITY_AUTO_RESTART_METRICS,
} from "@constants/adminConfigurationConstants";
import {
  AdminConfigurations,
  AdminConfigurationsResponse,
  OcularMotilityAdminConfiguration,
  OcularMotilityPatternConfiguration,
  SmoothPursuitOrbitConfiguration,
  ReadingAssessmentPageChangeInteraction,
  ReliabilityAutoRestartRule,
  VisualFieldAutoRestartConfiguration,
} from "types/adminConfigurationTypes";

function clampInteger(value: unknown, min: number, max: number, fallback: number): number {
  const parsed = typeof value === "string" ? Number(value) : value;
  if (typeof parsed !== "number" || !Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, Math.round(parsed)));
}

function clampDecimal(value: unknown, min: number, max: number, fallback: number): number {
  const parsed = typeof value === "string" ? Number(value.trim()) : value;
  if (typeof parsed !== "number" || !Number.isFinite(parsed)) {
    return fallback;
  }
  const clamped = Math.min(max, Math.max(min, parsed));
  const factor = 10 ** OCULAR_MOTILITY_DECIMAL_PLACES;
  return Math.round(clamped * factor) / factor;
}

function normalizeOcularMotilityPattern(
  raw: unknown,
  fallback: OcularMotilityPatternConfiguration,
): OcularMotilityPatternConfiguration {
  const pattern = (raw ?? {}) as Partial<OcularMotilityPatternConfiguration>;
  return {
    patternSize: clampDecimal(
      pattern.patternSize,
      OCULAR_MOTILITY_FIELD_BOUNDS.patternSize.min,
      OCULAR_MOTILITY_FIELD_BOUNDS.patternSize.max,
      fallback.patternSize,
    ),
    speed: clampDecimal(
      pattern.speed,
      OCULAR_MOTILITY_FIELD_BOUNDS.speed.min,
      OCULAR_MOTILITY_FIELD_BOUNDS.speed.max,
      fallback.speed,
    ),
    pauseDuration: clampDecimal(
      pattern.pauseDuration,
      OCULAR_MOTILITY_FIELD_BOUNDS.pauseDuration.min,
      OCULAR_MOTILITY_FIELD_BOUNDS.pauseDuration.max,
      fallback.pauseDuration,
    ),
  };
}

function normalizeSmoothPursuit(
  raw: unknown,
  fallback: SmoothPursuitOrbitConfiguration,
): SmoothPursuitOrbitConfiguration {
  const orbit = (raw ?? {}) as Partial<SmoothPursuitOrbitConfiguration>;
  const fields = Object.keys(
    SMOOTH_PURSUIT_FIELD_BOUNDS,
  ) as (keyof SmoothPursuitOrbitConfiguration)[];
  return fields.reduce((accumulator, field) => {
    const bounds = SMOOTH_PURSUIT_FIELD_BOUNDS[field];
    return {
      ...accumulator,
      [field]: clampDecimal(orbit[field], bounds.min, bounds.max, fallback[field]),
    };
  }, {} as SmoothPursuitOrbitConfiguration);
}

function normalizeRule(raw: unknown, fallback: ReliabilityAutoRestartRule) {
  const rule = (raw ?? {}) as Partial<ReliabilityAutoRestartRule>;
  return {
    minErrorPercentage: clampInteger(
      rule.minErrorPercentage,
      MIN_RELIABILITY_ERROR_PERCENTAGE,
      MAX_RELIABILITY_ERROR_PERCENTAGE,
      fallback.minErrorPercentage,
    ),
    minPresentations: clampInteger(
      rule.minPresentations,
      MIN_RELIABILITY_PRESENTATIONS,
      MAX_RELIABILITY_PRESENTATIONS,
      fallback.minPresentations,
    ),
  };
}

function normalizeBoolean(raw: unknown, fallback: boolean): boolean {
  return typeof raw === "boolean" ? raw : fallback;
}

function normalizePageChangeInteraction(raw: unknown): ReadingAssessmentPageChangeInteraction {
  const isKnown = Object.values(ReadingAssessmentPageChangeInteraction).includes(
    raw as ReadingAssessmentPageChangeInteraction,
  );
  return isKnown
    ? (raw as ReadingAssessmentPageChangeInteraction)
    : DEFAULT_ADMIN_CONFIGURATIONS.readingAssessment.pageChangeInteraction;
}

export function hasStoredConfiguration(
  response: Pick<AdminConfigurationsResponse, "updatedAt"> | null | undefined,
): boolean {
  return Boolean(response?.updatedAt);
}

export interface AdminConfigurationsSaveState {
  loading: boolean;
  saving: boolean;
  isStored: boolean;
  isDirty: boolean;
}

export function canSaveAdminConfigurations({
  loading,
  saving,
  isStored,
  isDirty,
}: AdminConfigurationsSaveState): boolean {
  if (loading || saving) return false;
  return !isStored || isDirty;
}

export function extractApiErrorStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === "number" ? status : null;
}

export function normalizeAdminConfigurations(raw: unknown): AdminConfigurations {
  const source = (raw ?? {}) as Partial<AdminConfigurations>;
  const rawAutoRestart = (source.visualFieldAutoRestart ?? {}) as Partial<
    Record<keyof VisualFieldAutoRestartConfiguration, unknown>
  >;

  const visualFieldAutoRestart = RELIABILITY_AUTO_RESTART_METRICS.reduce(
    (accumulator, metric) => ({
      ...accumulator,
      [metric]: normalizeRule(
        rawAutoRestart[metric],
        DEFAULT_ADMIN_CONFIGURATIONS.visualFieldAutoRestart[metric],
      ),
    }),
    {
      countdownSeconds: clampInteger(
        rawAutoRestart.countdownSeconds,
        MIN_RELIABILITY_COUNTDOWN_SECONDS,
        MAX_RELIABILITY_COUNTDOWN_SECONDS,
        DEFAULT_ADMIN_CONFIGURATIONS.visualFieldAutoRestart.countdownSeconds,
      ),
    } as VisualFieldAutoRestartConfiguration,
  );

  const rawOcularMotility = (source.ocularMotility ?? {}) as Partial<
    Record<keyof OcularMotilityAdminConfiguration, unknown>
  >;

  const ocularMotility: OcularMotilityAdminConfiguration = {
    xih: normalizeOcularMotilityPattern(
      rawOcularMotility.xih,
      DEFAULT_ADMIN_CONFIGURATIONS.ocularMotility.xih,
    ),
    smoothPursuit: normalizeSmoothPursuit(
      rawOcularMotility.smoothPursuit,
      DEFAULT_ADMIN_CONFIGURATIONS.ocularMotility.smoothPursuit,
    ),
  };

  return {
    readingAssessment: {
      pageChangeInteraction: normalizePageChangeInteraction(
        source.readingAssessment?.pageChangeInteraction,
      ),
    },
    visualFieldAutoRestart,
    ocularMotility,
    visualFieldFixationLoss: {
      movingFixationCatchTrials: normalizeBoolean(
        source.visualFieldFixationLoss?.movingFixationCatchTrials,
        DEFAULT_ADMIN_CONFIGURATIONS.visualFieldFixationLoss.movingFixationCatchTrials,
      ),
    },
  };
}
